import { Component, effect, ElementRef, OnInit, signal, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { of, switchMap, tap } from 'rxjs';
import { getTimeUTC } from 'src/app/core/helpers/date/date';
import { PreviousPageService } from 'src/app/core/services/previous-page.service';
import { Modal, ModalService } from 'src/app/project/components/modal/modal.service';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventReminders,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { TranslationService } from 'src/app/project/services/translation.service';
import { transformDate } from 'src/app/project/shared/date-transformer';
import { EDateFormat } from 'src/app/project/shared/enums/date-format.enum';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { PreferencesService } from '../../../preferences/preferences-service/preferences.service';
import { RecurringRemindersService } from '../../../reminders/recurring-reminders.service';
import {
  TReminder,
  TReminderActionDTO,
  TReminderDTO,
  TReminderSeries,
  TReminderUpdateDTO,
} from '../../../reminders/reminders.consts';
import { RemindersService } from '../../../reminders/reminders.service';
import { getReminderPriorityData } from '../../../reminders/utils/get-reminder-priority-data';
import { getReminderStatusIcon } from '../../../reminders/utils/get-reminder-status-icon';
import { EUserRole } from '../../../share/share-utils/user-roles';
import { SharesService } from '../../../share/shares.service';
import { UserService } from '../../../user/user.service';
import { TPointLightweight } from '../../points.model';
import { PointsService } from '../../points.service';
import { EPriority } from '../../priorities';
import { generateTimeText } from '../../utils/generate-time-text';
import { EEditReminderOptions } from './point-reminder-modal-edit-reminder-button/point-reminder-modal-edit-reminder-button-consts';
import { TReminderModalData } from './point-reminder-modal.consts';

@Component({
  selector: 'pp-point-reminder-modal',
  templateUrl: './point-reminder-modal.component.html',
  styleUrls: ['./point-reminder-modal.component.scss'],
  standalone: false,
})
export class PointReminderModalComponent implements OnInit {
  @ViewChild('body') reminderModalBody: ElementRef<HTMLDivElement>;

  isEditing = true;
  reminder: TReminder;
  reminderSeries: TReminderSeries = {
    frequency: null,
    startDateEpochMillis: null,
    endDateEpochMillis: null,
    id: null,
  };
  private reminderSeriesBackup: TReminderSeries = {
    frequency: null,
    startDateEpochMillis: null,
    endDateEpochMillis: null,
    id: null,
  };
  modal: Modal = this.modalService.getModal();

  nameInput = signal<string>('');
  descriptionInput = signal<string>('');
  timeInput = signal<string>(this.getNextFull15Minutes(new Date()));
  dateInput = signal<Date>(new Date());
  isPastDate = false;

  timeStampBackup: number;
  pointIdBackup: string;
  EIconPath = EIconPath;
  priorityName = '';
  reminderBackup: TReminder;

  pointId = '';
  pointTitle = '';

  statusIconSrc: EIconPath;

  date: string;
  time: string;

  isClosing = false;
  isNew = true;
  isDeleting = false;
  reminderDeleteOption: EEditReminderOptions = EEditReminderOptions.SINGLE;
  isEdited = false;
  isSnoozed = false;
  reminderSnoozeTime = '';
  isPlainReminder: boolean;
  editingMode: EEditReminderOptions;
  isNameInputFocused = false;
  initialDate: Date;
  EEditReminderOptions = EEditReminderOptions;
  editable: boolean;
  pointPermission: EUserRole = null;
  EUserRole = EUserRole;

  characterLimit = 60;

  constructor(
    private modalService: ModalService,
    private remindersService: RemindersService,
    private router: Router,
    private pointsService: PointsService,
    private preferencesService: PreferencesService,
    private translationService: TranslationService,
    private previousPageService: PreviousPageService,
    private recurringReminderService: RecurringRemindersService,
    private userService: UserService,
    private sharesService: SharesService,
  ) {
    effect(() => {
      this.update();
    });
  }

  update(): void {
    this.isEdited = this.changesMadeToInput();
    this.checkIfDateTimeIsOld();
  }

  ngOnInit(): void {
    const modalData: TReminderModalData = this.modal.data;
    this.pointId = modalData.pointId || modalData.reminder?.pointId;

    if (modalData.reminder) {
      this.pointTitle = modalData.reminder.pointTitle;
      this.isNew = false;
      this.isEditing = false;
      this.reminder = { ...modalData.reminder };
      this.pointId = modalData.reminder.pointId;
      this.isPlainReminder = !this.pointId;
      this.timeStampBackup = this.reminder.timestampEpochMillis;
      this.pointIdBackup = this.reminder.pointId;
      this.formatTimestamp();

      if (this.reminder.seriesId) {
        this.fetchRecurringReminderData();
      }
    } else if (this.pointId) {
      const point = this.pointsService.findPoint(this.pointId);
      this.pointTitle = point.title;
      this.timeInput.set(this.getNextFull15Minutes(new Date()));
      this.dateInput.set(new Date());

      this.reminder = {
        name: '',
        description: '',
        timestampEpochMillis: this.generateTimestamp(),
        alertEpochMillis: 0,
        pointId: this.pointId,
        pointTitle: point.title,
        reminderId: '',
        targetUserId: this.userService.getUser().userId,
        pointPriority: point.priority,
        pointStatus: point.status,
        workspaceId: point.workspaceRef.id,
        dismissed: false,
        assignees: [],
      };

      this.pointIdBackup = this.reminder.pointId;

      this.formatTimestamp();
    } else {
      if (modalData.timestamp) {
        this.dateInput.set(new Date(modalData.timestamp));

        if (modalData.skipTime) {
          this.timeInput.set(this.getNextFull15Minutes(new Date()));
        } else {
          this.timeInput.set(this.getNextFull15Minutes(new Date(modalData.timestamp)));
        }
      } else {
        this.timeInput.set(this.getNextFull15Minutes(new Date()));
        this.dateInput.set(new Date());
      }

      this.reminder = {
        name: '',
        description: '',
        timestampEpochMillis: this.generateTimestamp(),
        alertEpochMillis: 0,
        pointId: null,
        pointTitle: null,
        reminderId: '',
        targetUserId: this.userService.getUser().userId,
        pointPriority: null,
        pointStatus: null,
        workspaceId: null,
        dismissed: false,
        assignees: [],
      };

      this.pointIdBackup = this.reminder.pointId;
    }

    this.isSnoozed =
      this.reminder.alertEpochMillis !== this.reminder.timestampEpochMillis &&
      !this.reminder.dismissed;

    if (this.isSnoozed) {
      const dateFormat = this.preferencesService.getPreferences().dateFormat;
      this.reminderSnoozeTime = generateTimeText(this.reminder.alertEpochMillis, dateFormat);
    }

    if (this.pointId) {
      this.generatePointData();
    }

    const date = new Date(this.dateInput());
    const [hours, minutes] = this.timeInput().split(':').map(Number);
    date.setHours(hours, minutes, 0, 0);

    this.initialDate = date;
    this.editable = this.userService.getUser().userId === this.reminder.targetUserId;

    if (!this.editable) {
      this.reminderDeleteOption = EEditReminderOptions.SERIES;
    }
  }

  getPriorityClass(priority: EPriority): { [key: string]: boolean } {
    return {
      lowPriority: priority === EPriority.LOW,
      mediumPriority: priority === EPriority.MEDIUM,
      highPriority: priority === EPriority.HIGH,
    };
  }

  viewPoint(): void {
    this.previousPageService.setPreviousPageOnReminderOpen(this.router.url);

    const URL = ['/site', this.reminder.workspaceId, 'point', this.reminder.pointId];
    this.router.navigate(URL);
    this.close(true);
  }

  toggleEdit(mode: EEditReminderOptions): void {
    this.generateDateTimeForInput(new Date(this.reminder.timestampEpochMillis));
    this.nameInput.set(this.reminder.name);
    this.descriptionInput.set(this.reminder.description);
    this.pointTitle = this.reminder.pointTitle;
    this.reminderBackup = { ...this.reminder };
    this.isEditing = true;
    this.editingMode = mode;
  }

  selectPoint(body: { point: TPointLightweight; workspaceId: string }): void {
    if (body) {
      this.pointId = body.point.id;
      this.reminder.pointId = body.point.id;
      this.reminder.pointTitle = body.point.title;
      this.reminder.assignees = [];
      this.reminder.workspaceId = body.workspaceId;
    } else {
      this.pointId = '';
      this.reminder.pointId = '';
      this.reminder.pointTitle = '';
      this.reminder.assignees = [];
      this.reminder.workspaceId = null;
    }
    this.update();
    this.checkPointPermission();
  }

  submit(): void {
    const timestamp = this.generateTimestamp();

    if (this.reminder?.reminderId) {
      this.editReminder(timestamp);
    } else {
      this.createReminder(timestamp);
    }

    this.close();
  }

  onScrollToBottom(): void {
    if (this.reminderModalBody) {
      this.reminderModalBody.nativeElement.scrollTo({
        top: this.reminderModalBody.nativeElement.scrollHeight,
        behavior: 'instant',
      });
    }
  }

  private createReminder(timestamp: number): void {
    logEventInGTAG(EGoogleEventReminders.REMINDERS_ADD_REMINDER, {
      source: EGoogleEventCategory.REMINDERS,
    });
    const newReminder: TReminderDTO = {
      name: this.nameInput(),
      description: this.descriptionInput(),
      timestampEpochMillis: getTimeUTC(timestamp),
      pointId: this.pointId,
      assignees: this.reminder.assignees, // TODO Check is is right
    };

    if (this.reminderSeries.frequency) {
      this.recurringReminderService
        .createRecurringReminder(
          newReminder,
          this.reminderSeries.frequency,
          getTimeUTC(this.reminderSeries.endDateEpochMillis),
        )
        .pipe(
          tap((response) => {
            this.remindersService.fetchRemindersForSeries(response.id).subscribe();
          }),
        )
        .subscribe();
    } else {
      this.remindersService.createReminder(newReminder).subscribe();
    }
  }

  private editReminder(timestamp: number): void {
    const editedReminder: TReminderUpdateDTO = {
      name: this.nameInput(),
      description: this.descriptionInput(),
    };

    editedReminder.timestampEpochMillis = getTimeUTC(timestamp);

    editedReminder.pointId = this.reminder.pointId;
    editedReminder.assignees = this.reminder.assignees;

    if (!editedReminder.pointId) {
      editedReminder.pointId = '';
    }

    if (this.reminder.seriesId) {
      const seriesId = this.reminder.seriesId;

      switch (this.editingMode) {
        case EEditReminderOptions.SERIES: {
          this.recurringReminderService
            .editRecurringReminder({
              reminderId: this.reminder.reminderId,
              updatedReminder: editedReminder,
              frequency: this.reminderSeries.frequency,
              seriesId: seriesId,
              endDateEpochMillis: getTimeUTC(this.reminderSeries.endDateEpochMillis),
              startDateEpochMillis: editedReminder.timestampEpochMillis,
            })
            .pipe(
              switchMap((response) =>
                this.remindersService
                  .fetchRemindersForSeries(seriesId)
                  .pipe(switchMap(() => of(response))),
              ),
              switchMap((response) => this.remindersService.fetchRemindersForSeries(response.id)),
            )
            .subscribe();
          break;
        }
        case EEditReminderOptions.SINGLE: {
          this.remindersService
            .editReminder(this.reminder.reminderId, editedReminder)
            .pipe(switchMap(() => this.remindersService.fetchRemindersForSeries(seriesId)))
            .subscribe();

          break;
        }
      }
    } else {
      this.remindersService
        .editReminder(this.reminder.reminderId, editedReminder)
        .pipe(
          tap(() => {
            if (this.reminderSeries.frequency) {
              this.recurringReminderService
                .updateReminderToRecurring(
                  this.reminder.reminderId,
                  this.reminderSeries.frequency,
                  this.reminder.timestampEpochMillis,
                  getTimeUTC(this.reminderSeries.endDateEpochMillis),
                )
                .pipe(
                  tap((reminderSeries) => {
                    this.remindersService.fetchRemindersForSeries(reminderSeries.id).subscribe();
                  }),
                )
                .subscribe();
            } else {
              return of(null);
            }
          }),
        )
        .subscribe();
    }
  }

  cancel(): void {
    if (!this.changesMadeToInput()) {
      if (this.isNew) {
        this.close();
      } else {
        this.discardChanges();
      }
    } else {
      this.toggleIsClosing();
    }
  }

  discardChanges(): void {
    if (this.isNew) {
      this.close();
      return;
    }

    this.isEditing = false;
    this.isClosing = false;
    this.reminder = { ...this.reminderBackup };
  }

  toggleIsClosing(): void {
    if (this.isClosing) {
      this.isClosing = false;
      this.isDeleting = false;
      this.isClosing = false;
    } else {
      this.isClosing = true;
    }
  }

  generateDateTimeForInput(date: Date): void {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    this.timeInput.set(`${hours}:${minutes}`);
    this.dateInput.set(date);
  }

  setDateInput(date: Date[]): void {
    this.dateInput.set(date[0]);
    const [hours, minutes] = this.timeInput().split(':').map(Number);
    date[0].setHours(hours, minutes, 0, 0);

    this.checkIfDateTimeIsOld();
  }

  checkIfDateTimeIsOld(): void {
    const date = new Date(this.dateInput());
    const [hours, minutes] = this.timeInput().split(':').map(Number);
    date.setHours(hours, minutes, 0, 0);

    if (!this.initialDate || date === this.initialDate) {
      return;
    }

    const today = new Date();
    this.isPastDate = date < today;
  }

  generateTimestamp(): number {
    const date = new Date(this.dateInput());

    if (this.timeInput() !== null) {
      const [hours, minutes] = this.timeInput().split(':').map(Number);
      date.setHours(hours, minutes, 0, 0);
    }

    return date.getTime();
  }

  dismiss(): void {
    const body: TReminderActionDTO = {
      reminderIds: [this.reminder.reminderId],
    };

    this.remindersService
      .dismissReminders(body)
      .pipe(
        tap(() => {
          this.close();
        }),
      )
      .subscribe();
  }

  delete(): void {
    this.isDeleting = true;
    this.toggleIsClosing();
  }

  changesMadeToInput(): boolean {
    if (
      this.reminder.name !== this.nameInput() ||
      this.reminder.description !== this.descriptionInput() ||
      this.reminder.timestampEpochMillis !== this.generateTimestamp() ||
      this.reminder.pointId !== this.pointIdBackup ||
      this.reminderSeries.frequency !== this.reminderSeriesBackup.frequency ||
      this.reminderSeries.endDateEpochMillis !== this.reminderSeriesBackup.endDateEpochMillis ||
      (this.reminderBackup && !isEqual(this.reminder?.assignees, this.reminderBackup?.assignees))
    ) {
      return true;
    }
    return false;
  }

  deleteConfirm(): void {
    this.remindersService.deleteReminder(this.reminder.reminderId).subscribe(() => this.close());
  }

  close(cancelled: boolean = false): void {
    this.modalService.hideModal(cancelled);
  }

  formatTimestamp(): void {
    const date = new Date(this.reminder.timestampEpochMillis);
    const preferences = this.preferencesService.getPreferences();
    const dateFormat = preferences.dateFormat;

    this.date = transformDate({
      value: dayjs(this.reminder.timestampEpochMillis),
      inputHourFormat: '',
      format: preferences.dateFormat,
      localTime: false,
    });

    this.time = date.toLocaleTimeString(this.translationService.getLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: dateFormat === EDateFormat.US || dateFormat === EDateFormat.FRIENDLY,
    });
  }

  setUsers(userIds: string[]): void {
    this.reminder.assignees = userIds;

    this.update();
  }

  private generatePointData(): void {
    this.statusIconSrc = getReminderStatusIcon(this.reminder.pointStatus);

    const priorityData = getReminderPriorityData(this.reminder.pointPriority);
    this.priorityName = priorityData.priorityText;
    this.checkPointPermission();
  }

  private getNextFull15Minutes(date: Date): string {
    const minutes = date.getMinutes();
    const extraMinutes = minutes === 0 ? 0 : 15 - (minutes % 15);
    date.setMinutes(minutes + extraMinutes);
    date.setSeconds(0);
    date.setMilliseconds(0);

    // Format HH:MM
    const hours = String(date.getHours()).padStart(2, '0');
    const mins = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${mins}`;
  }

  private fetchRecurringReminderData(): void {
    this.recurringReminderService
      .fetchRecurringReminders(this.reminder.seriesId)
      .pipe(
        tap((reminderSeries) => {
          this.reminderSeries = reminderSeries;
          this.reminderSeriesBackup = cloneDeep(reminderSeries);
        }),
      )
      .subscribe();
  }

  private checkPointPermission(): void {
    if (!this.reminder.workspaceId) {
      this.pointPermission = null;
      return;
    }

    const share = this.sharesService
      .getUserShares()
      .find((searchedShare) => searchedShare.workspaceId === this.reminder.workspaceId);

    this.pointPermission = share.shareOption as EUserRole;
  }
}
